import { defineStore } from 'pinia';
import {
	FILTER_SPLITTER_VALUE_KEY,
	MAX_SPLITTER_VALUE_KEY,
	SPLITTER_VALUE_KEY,
} from 'src/stores/mainLayout/localStorageKeys';

import { getGlobalScreenEventBus } from 'src/services/screen/events/GlobalScreenEventBus';

import type { MainLayoutStoreState } from './interfaces';

const safeParseInt = (value: null | string, fallback: number): number => {
	if (value === null || value === 'null' || value === 'undefined' || value.trim() === '') {
		return fallback;
	}

	const parsed = parseInt(value, 10);
	return Number.isNaN(parsed) ? fallback : parsed;
};

const safeStringify = (value: null | number | undefined): string => {
	if (value === null || value === undefined || Number.isNaN(value)) {
		return '0';
	}

	return String(value);
};

const emitScreenContextChange = (source: string): void => {
	try {
		const eventBus = getGlobalScreenEventBus();
		eventBus.emitScreenContextChange(
			{
				monitor: { type: 'unknown' } as never,
				timestamp: Date.now(),
				viewport: {
					height: window.innerHeight,
					width: window.innerWidth,
				} as never,
			},
			source
		);
	} catch {
		// EventBus trigger failures are non-fatal — context-change is advisory.
	}
};

const createDefaultState = (): MainLayoutStoreState => ({
	filterSplitterValueState: safeParseInt(localStorage.getItem(FILTER_SPLITTER_VALUE_KEY), 0),
	maxSplitterValueState: safeParseInt(localStorage.getItem(MAX_SPLITTER_VALUE_KEY), 0),
	splitViewActiveState: false,
	splitterRestoreValueState: 553,
	splitterValueState: safeParseInt(localStorage.getItem(SPLITTER_VALUE_KEY), 920),
	urlOfExternalContentForTheIFrameState: null,
});

export const useMainLayoutStore = defineStore('mainLayout', {
	actions: {
		setFilterSplitterValue(sliderValue: null | number | undefined): void {
			localStorage.setItem(FILTER_SPLITTER_VALUE_KEY, safeStringify(sliderValue));

			if (sliderValue != null && !Number.isNaN(sliderValue)) {
				const clampedValue = Math.max(0, Math.min(sliderValue, 500));
				this.filterSplitterValueState = clampedValue;
			}
		},
		setMaxSplitterValue(sliderValue: null | number | undefined): void {
			localStorage.setItem(MAX_SPLITTER_VALUE_KEY, safeStringify(sliderValue));

			if (sliderValue != null && !Number.isNaN(sliderValue)) {
				const clampedValue = Math.max(800, sliderValue);
				this.maxSplitterValueState = clampedValue;
				emitScreenContextChange('Store-SET_MAX_SPLITTER_VALUE');
			}
		},
		setSplitViewActive(splitViewActive: boolean): void {
			this.splitViewActiveState = splitViewActive;
		},
		setSplitterRestoreValue(sliderValue: null | number | undefined): void {
			if (sliderValue != null && !Number.isNaN(sliderValue)) {
				const clampedValue = Math.max(490, sliderValue);
				this.splitterRestoreValueState = clampedValue;
			}
		},
		setSplitterValue(sliderValue: null | number | undefined): void {
			localStorage.setItem(SPLITTER_VALUE_KEY, safeStringify(sliderValue));

			if (sliderValue != null && !Number.isNaN(sliderValue)) {
				const dynamicMax =
					this.maxSplitterValueState && this.maxSplitterValueState > 800
						? this.maxSplitterValueState
						: Math.max(2000, window.screen?.availWidth - 60 || 2000);
				const clampedValue = Math.max(490, Math.min(sliderValue, dynamicMax));
				this.splitterValueState = clampedValue;
				emitScreenContextChange('Store-SET_SPLITTER_VALUE');
			}
		},
		setUrlOfExternalContentForTheIFrame(urlOfExternalContentForTheIFrame: null | string): void {
			this.urlOfExternalContentForTheIFrameState = urlOfExternalContentForTheIFrame;
		},
	},
	getters: {
		filterSplitterValue(state): number {
			const value = state.filterSplitterValueState;
			return Number.isNaN(value) ? 0 : value;
		},
		maxSplitterValue(state): number {
			const value = state.maxSplitterValueState;
			return Number.isNaN(value) || value <= 0 ? 1200 : value;
		},
		splitViewActive(state): boolean {
			return state.splitViewActiveState;
		},
		splitterRestoreValue(state): number {
			const value = state.splitterRestoreValueState;
			return Number.isNaN(value) ? 553 : value;
		},
		splitterValue(state): number {
			const value = state.splitterValueState;
			return Number.isNaN(value) ? 920 : value;
		},
		urlOfExternalContentForTheIFrame(state): null | string {
			return state.urlOfExternalContentForTheIFrameState;
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
